import { useCallback, useEffect, useState } from 'react';
import { api } from '../api';
import { Card, Spinner, useToast } from '../components/ui';
import type { Language, Member, PickupTemplate } from '../types';

const T = {
  en: {
    title: 'The school run',
    intro:
      'Set the normal week once. It keeps applying until you change it — you only ever need to touch a single day when something is different.',
    who: 'Picked up by',
    time: 'Time',
    place: 'Place',
    nobody: 'Not set',
    noPickup: 'N/A — no pickup',
    save: 'Save the usual week',
    saved: 'Saved. This is now the usual week.',
    clear: 'Clear one-off changes',
    clearHelp:
      'One-day changes stay put when you edit the usual week. This drops the ones from today onwards.',
    cleared: (n: number) =>
      n === 0 ? 'There were no one-off changes to clear' : `Cleared ${n} one-off change${n === 1 ? '' : 's'}`,
    noChildren: 'Add the children in the household first.',
    copyDown: 'Same all week',
    copyHelp: 'Copy Monday down to the rest of the week',
  },
  fr: {
    title: 'La sortie d’école',
    intro:
      'Réglez la semaine habituelle une fois. Elle s’applique jusqu’à ce que vous la changiez — il ne reste qu’à modifier un jour quand c’est différent.',
    who: 'Récupéré par',
    time: 'Heure',
    place: 'Lieu',
    nobody: 'Non défini',
    noPickup: 'N/A — pas de sortie',
    save: 'Enregistrer la semaine habituelle',
    saved: 'Enregistré. C’est maintenant la semaine habituelle.',
    clear: 'Effacer les changements ponctuels',
    clearHelp:
      'Les changements d’un jour restent quand vous modifiez la semaine habituelle. Ceci les supprime à partir d’aujourd’hui.',
    cleared: (n: number) =>
      n === 0 ? 'Aucun changement ponctuel à effacer' : `${n} changement(s) ponctuel(s) effacé(s)`,
    noChildren: 'Ajoutez d’abord les enfants du foyer.',
    copyDown: 'Pareil toute la semaine',
    copyHelp: 'Copier le lundi sur le reste de la semaine',
  },
} as const;

const DAYS = {
  en: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
  fr: ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi'],
} as const;

const key = (weekday: number, memberId: string) => `${weekday}:${memberId}`;

/** Today, in the local calendar rather than UTC. */
function today(): string {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60_000).toISOString().slice(0, 10);
}

/**
 * The standing weekly school run (§3).
 *
 * This is a household setting, not a weekly chore: who normally collects each
 * child, when, and from where. The Today module reads it and only ever needs a
 * one-day edit when something is out of the ordinary.
 *
 * Weekends are left out on purpose — school is Monday to Friday, and in France
 * Wednesday is often a half day, which the per-day times handle.
 */
export function SchoolRunPattern({ language, readOnly }: { language: Language; readOnly: boolean }) {
  const t = T[language];
  const toast = useToast();
  const [children, setChildren] = useState<Member[]>([]);
  const [carers, setCarers] = useState<string[]>([]);
  const [rows, setRows] = useState<Map<string, PickupTemplate> | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [household, templates] = await Promise.all([api.household(), api.pickupTemplates()]);
    setChildren(household.members.filter((m) => m.member_type === 'child'));
    setCarers(household.members.filter((m) => m.member_type !== 'child').map((m) => m.name));
    setRows(new Map(templates.map((row) => [key(row.weekday, row.memberId), row])));
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (!rows) {
    return (
      <Card title={t.title}>
        <Spinner />
      </Card>
    );
  }

  if (children.length === 0) {
    return (
      <Card title={t.title}>
        <p className="dim">{t.noChildren}</p>
      </Card>
    );
  }

  const cell = (weekday: number, memberId: string): PickupTemplate =>
    rows.get(key(weekday, memberId)) ?? {
      weekday,
      memberId,
      pickedUpBy: null,
      time: null,
      place: null,
      notRequired: false,
    };

  const edit = (weekday: number, memberId: string, patch: Partial<PickupTemplate>) => {
    setRows((current) => {
      const next = new Map(current);
      next.set(key(weekday, memberId), { ...cell(weekday, memberId), ...patch });
      return next;
    });
  };

  const copyMondayDown = () => {
    setRows((current) => {
      const next = new Map(current);
      for (const child of children) {
        const monday = next.get(key(0, child.id));
        for (let weekday = 1; weekday <= 4; weekday += 1) {
          if (!monday) next.delete(key(weekday, child.id));
          else next.set(key(weekday, child.id), { ...monday, weekday });
        }
      }
      return next;
    });
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.savePickupTemplates([...rows.values()]);
      toast(t.saved);
      void load();
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Could not save');
    } finally {
      setBusy(false);
    }
  };

  const clearOverrides = async () => {
    setBusy(true);
    try {
      const result = await api.clearPickupOverrides(today());
      toast(t.cleared(result.cleared));
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Could not clear');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title={t.title}>
      <p className="dim" style={{ marginBottom: 16 }}>
        {t.intro}
      </p>

      <div className="stack" style={{ gap: 18 }}>
        {DAYS[language].map((dayName, weekday) => (
          <div key={dayName} className="pattern-day">
            <h3 className="pattern-day-name">{dayName}</h3>
            {children.map((child) => {
              const value = cell(weekday, child.id);
              return (
                <div className="pattern-row" key={child.id}>
                  <span className="pattern-child">{child.name}</span>
                  <select
                    aria-label={`${child.name} — ${t.who}`}
                    value={value.notRequired ? '__none__' : value.pickedUpBy ?? ''}
                    disabled={readOnly || busy}
                    onChange={(event) => {
                      const chosen = event.target.value;
                      if (chosen === '__none__') {
                        edit(weekday, child.id, { notRequired: true, pickedUpBy: null });
                      } else {
                        edit(weekday, child.id, { notRequired: false, pickedUpBy: chosen || null });
                      }
                    }}
                  >
                    <option value="">{t.nobody}</option>
                    {carers.map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                    <option value="__none__">{t.noPickup}</option>
                  </select>
                  <input
                    type="time"
                    aria-label={`${child.name} — ${t.time}`}
                    value={value.time ?? ''}
                    disabled={readOnly || busy || value.notRequired}
                    onChange={(event) => edit(weekday, child.id, { time: event.target.value || null })}
                  />
                  <input
                    type="text"
                    aria-label={`${child.name} — ${t.place}`}
                    placeholder={t.place}
                    value={value.place ?? ''}
                    disabled={readOnly || busy || value.notRequired}
                    onChange={(event) => edit(weekday, child.id, { place: event.target.value || null })}
                  />
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {!readOnly && (
        <div className="stack" style={{ gap: 10, marginTop: 18 }}>
          <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
            <button className="btn primary" onClick={save} disabled={busy}>
              {t.save}
            </button>
            <button className="btn" onClick={copyMondayDown} disabled={busy} title={t.copyHelp}>
              {t.copyDown}
            </button>
          </div>
          <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
            <button className="btn" onClick={clearOverrides} disabled={busy}>
              {t.clear}
            </button>
          </div>
          <p className="dim" style={{ fontSize: 13, margin: 0 }}>
            {t.clearHelp}
          </p>
        </div>
      )}
    </Card>
  );
}
